import api from './base-api';

const usersApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// PUBLIC
		getUserScore: builder.query({
			query: ({ userId }: any) => {
				if (!userId) throw new Error('User ID is required.');
				return {
					url: `users/score/${userId}`,
				};
			},
			providesTags: (result: any, error: any, { userId }: any) => [
				{ type: 'UserScore', id: userId },
			],
			extraOptions: { skipAuth: true },
		}),
		getPublicUsersWithSmartRatings: builder.query({
			query: ({ category_id, ...filter }: any) => ({
				url: 'users/public/smart-rating',
				method: 'POST',
				body: { category_id, ...filter },
			}),
			providesTags: ['SmartRatings'],
			extraOptions: { skipAuth: true },
		}),
		// PRIVATE
		getUsersWithSmartRatings: builder.query({
			query: ({ category_id, ...filter }: any) => ({
				url: 'users/smart-rating',
				method: 'POST',
				body: { category_id, ...filter },
			}),
			providesTags: ['SmartRatings'],
		}),
		getUserSmartRatingHistory: builder.query({
			query: ({ userId, categoryId }: any) => {
				if (!categoryId) throw new Error('Category ID is required.');
				if (!userId) throw new Error('User ID is required.');
				return {
					url: `users/score/${userId}/smart-rating/${categoryId}`,
				};
			},
		}),
		updateUserCategories: builder.mutation({
			query: ({ userId, data }: any) => ({
				url: `/users/${userId}/categories`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['UserProfile'],
		}),
		updateProfile: builder.mutation({
			query: (data: any) => ({
				url: `users`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['UserProfile'],
		}),
		updateCookieSettings: builder.mutation({
			query: (data: any) => ({
				url: `users/cookie-settings`,
				method: 'PATCH',
				body: data,
			}),
			invalidatesTags: ['UserProfile'],
		}),
		updateUserLobbyTeams: builder.mutation({
			query: ({ userId, lobbyId, teams_ids }: any) => {
				if (!userId) throw new Error('User ID is required.');
				if (!lobbyId) throw new Error('Lobby ID is required.');
				const uniqueTeamIds = Array.isArray(teams_ids) ? Array.from(new Set(teams_ids)) : undefined;

				return {
					url: `/users/${userId}/lobbies/${lobbyId}/teams`,
					method: 'PUT',
					body: { teams_ids: uniqueTeamIds },
				};
			},
		}),
		getUserAchievements: builder.query({
			query: () => ({
				url: `users/achievements/fetch`,
			}),
			providesTags: ['UserAchievements'],
		}),
		getFirst25All: builder.query({
			query: () => ({
				url: `users/trades/check-25`,
			}),
		}),
		setAchievementDelivered: builder.mutation({
			query: (achievementId: any) => ({
				url: `users/achievements/${achievementId}/delivered`,
				method: 'PATCH',
			}),
			invalidatesTags: ['UserAchievements', 'ProbabilityAchievements'],
		}),
		setAchievementViewed: builder.mutation({
			query: (achievementId) => ({
				url: `users/achievements/${achievementId}/viewed`,
				method: 'PATCH',
			}),
			invalidatesTags: ['UserAchievements', 'ProbabilityAchievements'],
		}),
		setLastSeen: builder.mutation({
			query: () => ({
				url: `users/last-seen`,
				method: 'PATCH',
			}),
		}),
		claimAppReviewPrompt: builder.mutation({
			query: () => ({
				url: 'users/app-review-prompt/claim',
				method: 'POST',
			}),
			invalidatesTags: (result) => (result?.should_prompt ? ['UserProfile'] : []),
		}),
		getUserInsights: builder.query({
			query: ({
				period = 'weekly',
				categoryId,
				userId,
			}: { period?: string; categoryId?: any; userId?: any } = {}) => ({
				url: 'users/insights',
				params: { period, ...(categoryId && { categoryId }), ...(userId && { userId }) },
			}),
			providesTags: ['UserInsights'],
		}),
		updateAgreementToEmails: builder.mutation({
			query: (body) => ({
				url: `/users/update-agreement-to-notifications`,
				method: 'PATCH',
				body: body,
			}),
			invalidatesTags: ['UserProfile'],
		}),

		deleteUserAdmin: builder.mutation({
			query: (id) => ({
				url: `/users/${id}`,
				method: 'DELETE',
			}),
		}),
	}),
});

export const {
	useGetUserSmartRatingHistoryQuery,
	useGetUserScoreQuery,
	useGetPublicUsersWithSmartRatingsQuery,
	useGetUsersWithSmartRatingsQuery,
	useUpdateUserCategoriesMutation,
	useUpdateProfileMutation,
	useUpdateCookieSettingsMutation,
	useUpdateUserLobbyTeamsMutation,
	useGetUserAchievementsQuery,
	useSetAchievementDeliveredMutation,
	useSetAchievementViewedMutation,
	useSetLastSeenMutation,
	useClaimAppReviewPromptMutation,
	useGetFirst25AllQuery,
	useGetUserInsightsQuery,
	useUpdateAgreementToEmailsMutation,
	useDeleteUserAdminMutation,
} = usersApi;
